import React, { createContext, useContext, useState, useCallback, useRef } from 'react';

interface PreloadState {
  [routePath: string]: {
    isPreloaded: boolean;
    isLoading: boolean;
    priority: 'high' | 'medium' | 'low';
    loadedAt?: number;
  };
}

interface PreloadContextType {
  preloadState: PreloadState;
  startPreloading: () => void;
  cancelPreloading: () => void;
  isPreloading: boolean;
  preloadProgress: number;
  isRoutePreloaded: (routePath: string) => boolean;
}

const PreloadContext = createContext<PreloadContextType | undefined>(undefined);

// Route configuration with priorities
const ROUTE_CONFIG = {
  // High priority - most used pages
  '/dashboard': { priority: 'high' as const, delay: 0 },
  '/transacoes': { priority: 'high' as const, delay: 500 },
  '/lancamentos': { priority: 'high' as const, delay: 1000 },
  
  // Medium priority - frequently used
  '/contas': { priority: 'medium' as const, delay: 1500 },
  '/cartoes': { priority: 'medium' as const, delay: 2000 },
  '/categorias': { priority: 'medium' as const, delay: 2500 },
  '/metas': { priority: 'medium' as const, delay: 1200 }, // Moved to medium priority with lower delay
  '/agenda': { priority: 'medium' as const, delay: 3000 },
  
  // Low priority - less frequently used
  '/relatorios': { priority: 'low' as const, delay: 3500 },
  '/orcamentos': { priority: 'low' as const, delay: 4000 },
  '/configuracoes': { priority: 'low' as const, delay: 5000 },
  '/ajuda-e-suporte': { priority: 'low' as const, delay: 5500 },
  '/documentacao': { priority: 'low' as const, delay: 6000 },
  '/admin': { priority: 'low' as const, delay: 6500 }
};

export const PreloadProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [preloadState, setPreloadState] = useState<PreloadState>(() => {
    // Initialize state with all routes
    const initialState: PreloadState = {};
    Object.entries(ROUTE_CONFIG).forEach(([route, config]) => {
      initialState[route] = {
        isPreloaded: false,
        isLoading: false,
        priority: config.priority
      };
    });
    return initialState;
  });
  
  const [isPreloading, setIsPreloading] = useState(false);
  const [preloadProgress, setPreloadProgress] = useState(0);
  const timeoutsRef = useRef<NodeJS.Timeout[]>([]);
  const abortControllerRef = useRef<AbortController | null>(null);

  const preloadRoute = useCallback(async (routePath: string) => {
    if (abortControllerRef.current?.signal.aborted) return;
    
    // Use functional update to avoid dependency on preloadState
    let shouldProceed = false;
    setPreloadState(prev => {
      const routeState = prev[routePath];
      if (routeState?.isPreloaded || routeState?.isLoading) {
        shouldProceed = false;
        return prev;
      }
      shouldProceed = true;
      return {
        ...prev,
        [routePath]: {
          ...prev[routePath],
          isLoading: true
        }
      };
    });

    if (!shouldProceed) return;

    try {
      console.log(`Starting preload for: ${routePath}`);
      const startTime = performance.now();
      
      // Preload both the lazy wrapper and the actual component
      switch (routePath) {
        case '/relatorios':
          const [lazyReports, actualReports] = await Promise.all([
            import('@/pages/lazy/LazyReports'),
            import('@/pages/Reports')
          ]);
          break;
        case '/cartoes':
          const [lazyCards, actualCards] = await Promise.all([
            import('@/pages/lazy/LazyCards'),
            import('@/pages/Cards')
          ]);
          break;
        case '/contas':
          const [lazyAccounts, actualAccounts] = await Promise.all([
            import('@/pages/lazy/LazyAccounts'),
            import('@/pages/Accounts')
          ]);
          break;
        case '/categorias':
          const [lazyCategories, actualCategories] = await Promise.all([
            import('@/pages/lazy/LazyCategories'),
            import('@/pages/Categories')
          ]);
          break;
        case '/orcamentos':
          const [lazyBudgets, actualBudgets] = await Promise.all([
            import('@/pages/lazy/LazyBudgets'),
            import('@/pages/Budgets')
          ]);
          break;
        case '/metas':
          const [lazyGoals, actualGoals] = await Promise.all([
            import('@/pages/lazy/LazyGoals'),
            import('@/pages/Goals')
          ]);
          break;
        case '/configuracoes':
          const [lazySettings, actualSettings] = await Promise.all([
            import('@/pages/lazy/LazySettings'),
            import('@/pages/Settings')
          ]);
          break;
        case '/ajuda-e-suporte':
          const [lazyHelp, actualHelp] = await Promise.all([
            import('@/pages/lazy/LazyHelpSupport'),
            import('@/pages/HelpSupport')
          ]);
          break;
        case '/documentacao':
          const [lazyDocs, actualDocs] = await Promise.all([
            import('@/pages/lazy/LazyDocumentation'),
            import('@/pages/Documentation')
          ]);
          break;
        // Critical pages (already loaded, just mark as preloaded)
        case '/dashboard':
        case '/transacoes':
        case '/lancamentos':
        case '/agenda':
        case '/admin':
          break;
        default:
          break;
      }
      
      const loadTime = performance.now() - startTime;
      console.log(`Preloaded ${routePath} in ${loadTime.toFixed(2)}ms`);

      if (abortControllerRef.current?.signal.aborted) return;

      // Mark as preloaded
      setPreloadState(prev => ({
        ...prev,
        [routePath]: {
          ...prev[routePath],
          isPreloaded: true,
          isLoading: false,
          loadedAt: Date.now()
        }
      }));

    } catch (error) {
      if (abortControllerRef.current?.signal.aborted) return;
      
      console.warn(`Failed to preload route ${routePath}:`, error);
      
      // Mark as failed (not loading anymore)
      setPreloadState(prev => ({
        ...prev,
        [routePath]: {
          ...prev[routePath],
          isLoading: false
        }
      }));
    }
  }, []); // Remove preloadState dependency to fix circular dependency

  const startPreloading = useCallback(() => {
    // Don't start if already preloading
    if (isPreloading) return;
    
    // Check if user prefers reduced data usage
    if ('connection' in navigator && (navigator as any).connection?.saveData) {
      console.log('Save data mode detected, skipping preload');
      return;
    }

    setIsPreloading(true);
    setPreloadProgress(0);
    
    // Create abort controller for this preload session
    abortControllerRef.current = new AbortController();
    
    const routes = Object.entries(ROUTE_CONFIG);
    let completedRoutes = 0;
    
    // Clear any existing timeouts
    timeoutsRef.current.forEach(timeout => clearTimeout(timeout));
    timeoutsRef.current = [];
    
    routes.forEach(([route, config], index) => {
      const timeout = setTimeout(async () => {
        if (abortControllerRef.current?.signal.aborted) return;
        
        // Use requestIdleCallback if available for better performance
        const executePreload = () => {
          preloadRoute(route).finally(() => {
            completedRoutes++;
            setPreloadProgress((completedRoutes / routes.length) * 100);
            
            // Complete preloading when all routes are done
            if (completedRoutes === routes.length) {
              setIsPreloading(false);
            }
          });
        };

        if ('requestIdleCallback' in window) {
          requestIdleCallback(executePreload, { timeout: 1000 });
        } else {
          executePreload();
        }
      }, config.delay);
      
      timeoutsRef.current.push(timeout);
    });
  }, [isPreloading, preloadRoute]);

  const cancelPreloading = useCallback(() => {
    // Abort ongoing operations
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    
    // Clear all timeouts
    timeoutsRef.current.forEach(timeout => clearTimeout(timeout));
    timeoutsRef.current = [];
    
    // Reset state
    setIsPreloading(false);
    setPreloadProgress(0);
    
    // Reset loading states
    setPreloadState(prev => {
      const newState = { ...prev };
      Object.keys(newState).forEach(route => {
        if (newState[route].isLoading) {
          newState[route].isLoading = false;
        }
      });
      return newState;
    });
  }, []);

  const isRoutePreloaded = useCallback((routePath: string) => {
    return preloadState[routePath]?.isPreloaded || false;
  }, [preloadState]);

  const value = {
    preloadState,
    startPreloading,
    cancelPreloading,
    isPreloading,
    preloadProgress,
    isRoutePreloaded
  };

  return (
    <PreloadContext.Provider value={value}>
      {children}
    </PreloadContext.Provider>
  );
};

export const usePreload = () => {
  const context = useContext(PreloadContext);
  if (context === undefined) {
    throw new Error('usePreload must be used within a PreloadProvider');
  }
  return context;
};